import { useTranslation } from "react-i18next";
import { outsideExpected } from "../expected";
import { Frame, key, Note, useWidgetText } from "../shared";
import type { WidgetProps } from "../types";

/** value_grid: up to twelve readings, one cell each, with the newest age once; amber outside the band (DDB-03). */
export function ValueGrid({ widget, input, now }: WidgetProps) {
  const fmt = useWidgetText();
  const { t } = useTranslation();
  const cfg = widget.config ?? {};
  const columns = typeof cfg.columns === "number" ? cfg.columns : 2;
  const cells = (widget.bindings ?? []).flatMap((b) =>
    b.datapoint ? [{ b, p: input.points?.[key(b.device, b.datapoint)] }] : [],
  );
  const times = cells.flatMap((c) => (c.p?.latest ? [c.p.latest.at] : [])).sort();
  const newest = times.at(-1);
  const offline = cells.some((c) => input.devices?.[c.b.device]?.state === "offline");
  const state = offline ? "offline" : newest ? "normal" : "empty";
  return (
    <Frame widget={widget} state={state} title={widget.title}>
      <dl className="grid gap-2" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
        {cells.map(({ b, p }) => {
          const v = p?.latest?.value;
          const text = p && typeof v === "number" ? fmt.value(v, p) : fmt.t("state.noData");
          const outside = outsideExpected(p, v);
          return (
            <div key={key(b.device, b.datapoint ?? "")} {...(outside ? { "data-expected": "outside" } : {})}>
              <dt className="text-xs text-gray-700">{b.label ?? p?.name}</dt>
              <dd className={`text-lg font-semibold tabular-nums ${outside ? "text-amber-800" : "text-gray-900"}`}>
                {text}
                {outside && <span className="sr-only">, {t("widgets.outsideExpected")}</span>}
              </dd>
              {outside && (
                <dd aria-hidden="true" className="text-xs text-amber-900">
                  {t("widgets.outsideExpected")}
                </dd>
              )}
            </div>
          );
        })}
      </dl>
      {cfg.show_age !== false && newest && <Note>{fmt.age(newest, now)}</Note>}
      {offline && <Note tone="warn">{fmt.t("state.offline")}</Note>}
    </Frame>
  );
}
